import Swiper, { A11y, Autoplay, Controller, EffectCards, EffectCoverflow, EffectCreative, EffectCube, EffectFade, EffectFlip, FreeMode, Grid, HashNavigation, History, Keyboard, Lazy, Manipulation, Mousewheel, Navigation, Pagination, Parallax, Scrollbar, Thumbs, Virtual, Zoom } from 'swiper';
import AOS from 'aos';

class EventShowcase {
  /**
   * @param {HTMLElement} container
   * @param {{condition: URLSearchParams, grouping: 'area'|'category'|'maker', groupName: string|null, readAllLabel: string, onEventLoaded: function}} options
   */
  constructor(container, options) {
    /**
     * @type {HTMLElement}
     */
    this.container = container;

    /**
     * @type {URLSearchParams}
     */
    this.condition = options.condition;

    /**
     * @type {'area'|'category'|'maker'}
     */
    this.grouping = options.grouping ?? 'area';

    /**
     * @type {string|null}
     */
    this.groupName = options.groupName;

    /**
     * @type {string}
     */
    this.readAllLabel = options.readAllLabel ?? options.area_title ?? 'のイベントを探す';

    /**
     * @type {object}
     */
    this.onEventLoaded = options.onEventLoaded || (() => {
    });
  }

  /**
   * イベント一覧を Seiloo API から取得します。
   *
   * @param {URLSearchParams} condition
   * @return {Promise<Array<Object>>}
   */
  async fetchEvents(condition) {
    // Seiloo API イベント一覧取得用のエンドポイント
    const url = new URL(window.location.protocol + '//' + window.location.host + '/api/v1/events');

    return fetch(url + '?' + condition.toString())
      .then(response => response.json())
      .then(response => response.data);
  }

  /**
   * イベントのリストをエリア毎に分割した配列へグルーピングします。
   *
   * @param {Array<Object>} events
   * @param {string} method
   * @return {Array<Object>}
   */
  groupEvent(events, method) {
    switch (method) {
      case 'area':
        return this.groupEventByArea(events);

      case 'maker':
        return this.groupEventByMaker(events);

      case 'category':
        return this.groupEventByCategory(events);

      case 'none':
        return this.groupEventByNone(events);

      default:
        throw new Error('Unknown value given to \'method\' argument.');
    }
  }

  /**
   * イベントのリストをエリア毎に分割した配列へグルーピングします。
   *
   * @param {Array<Object>} events
   * @return {Array<Object>}
   */
  groupEventByArea(events) {
    const groups = events.reduce((groups, event) => {
      const area = event.site_prefecture.area;

      if (!(area.id in groups)) {
        const params = new URLSearchParams(this.condition);
        params.delete('limit');

        groups[area.id] = {
          name: area.name,
          id: area.id,
          area: area,
          events: [],
          url: `/area/${encodeURIComponent(area.slug)}?` + params.toString(),
        };
      }

      const group = groups[area.id];
      group.events.push(event);

      return groups;
    }, {});

    return Object.values(groups);
  }

  /**
   * イベントのリストをカテゴリー毎に分割した配列へグルーピングします。
   *
   * @param {Array<Object>} events
   * @return {Array<Object>}
   */
  groupEventByCategory(events) {
    const groups = events.reduce((groups, event) => {
      return (event.categories ?? []).reduce((groups, category) => {
        if (!(category.id in groups)) {
          const params = new URLSearchParams(this.condition);
          params.append('category_slug[]', category.slug);
          params.delete('limit');

          groups[category.id] = {
            name: category.name,
            id: category.id,
            category: category,
            events: [],
            url: '/events?' + params.toString(),
          };
        }

        const group = groups[category.id];
        group.events.push(event);

        return groups;
      }, groups);
    }, {});

    return Object.values(groups);
  }

  /**
   * イベントのリストをメーカー毎に分割した配列へグルーピングします。
   *
   * @param {Array<Object>} events
   * @return {Array<Object>}
   */
  groupEventByMaker(events) {
    const groups = events.reduce((groups, event) => {
      return (event.makers ?? []).reduce((groups, maker) => {
        if (!(maker.id in groups)) {
          const params = new URLSearchParams(this.condition);
          params.append('maker_slug[]', maker.slug);
          params.delete('limit');

          groups[maker.id] = {
            name: maker.name,
            id: maker.id,
            maker: maker,
            events: [],
            url: '/events?' + params.toString(),
          };
        }

        const group = groups[maker.id];
        group.events.push(event);

        return groups;
      }, groups);
    }, {});

    return Object.values(groups);
  }

  /**
   * 無分類のイベントのリストを1つにグルーピングします。
   *
   * @param {Array<Object>} events
   * @return {Array<Object>}
   */
  groupEventByNone(events) {
    const params = new URLSearchParams(this.condition);

    return  [
      {
          name: '',
          id: '',
          events: events,
          url: '/events?' + params.toString(),
        },
    ];
  }

  /**
   * イベントのリストをエリア毎に分割した配列へグルーピングします。
   *
   * @param {Array<Object>} events
   * @param {string} method
   * @return {Array<Object>}
   */
  sortGroup(events, method) {
    switch (method) {
      case 'area':
        return this.sortGroupByArea(events);

      case 'category':
        return this.sortGroupByCategory(events);

      case 'maker':
        return this.sortGroupByMaker(events);

      case 'none':
        return this.sortGroupByNone(events);

      default:
        throw new Error('Unknown value given to \'method\' argument.');
    }
  }

  /**
   * エリア別のイベントグループを並び替えます。
   *
   * @param {Array<Object>} groups
   */
  sortGroupByArea(groups) {
    const priorities = new Map([
      [3, 1], // 関東
      [4, 2], // 近畿
      [5, 3], // 中部
      [1, 4], // 北海道
      [2, 5], // 東北
      [6, 6], // 北信越
      [7, 7], // 中国
      [9, 8], // 四国
      [8, 9], // 九州・沖縄
    ]);

    groups.sort((a, b) => {
      const aPriority = priorities.get(a.area.id);
      const bPriority = priorities.get(b.area.id);

      return aPriority - bPriority;
    });

    return groups;
  }

  /**
   * カテゴリー別のイベントグループを並び替えます。
   *
   * @param {Array<Object>} groups
   */
  sortGroupByCategory(groups) {
    groups.sort((a, b) => a.category.weight - b.category.weight);

    return groups;
  }

  /**
   * メーカー別のイベントグループを並び替えます。
   *
   * @param {Array<Object>} groups
   */
  sortGroupByMaker(groups) {
    groups.sort((a, b) => a.maker.name.localeCompare(b.maker.name));

    return groups;
  }

  /**
   * 無分類のイベントグループを並び替えます。
   *
   * @param {Array<Object>} groups
   */
  sortGroupByNone(groups) {
    return groups;
  }

  /**
   * イベントリスト用サムネイルの画面を構築します。
   * @param {Object} event
   * @return {HTMLElement}
   */
  buildEventView(event) {
    const title = document.createElement('p');
    title.classList.add('event-showcase__event-title');
    title.textContent = event.site_prefecture?.name + event.site_city;

    const image = document.createElement('img');
    image.classList.add('event-showcase__event-image');
    //image.loading = 'lazy';
    image.src = event.cover_url;
    image.alt = event.title;

    const view = document.createElement('a');
    view.classList.add('event-showcase__event');
    view.href = event.url;
    view.target = '_blank';
    view.appendChild(image);
    view.appendChild(title);

    return view;
  }

  /**
   * エリア別にグループ分けされたイベントリストの画面を構築します。
   *
   * @param {Object} group
   * @param {URLSearchParams} condition
   * @param {string} groupName
   * @param {string} readAllLabel
   * @return {HTMLElement}
   */
  buildGroupView(group, condition, groupName, readAllLabel) {
    const view = document.createElement('div');
    view.classList.add('event-showcase__group', 'view_area_id__' + group.id);
    view.setAttribute('id', 'area_id__' + group.id);

    const groupTitleText = document.createElement('span');
    groupTitleText.classList.add('event-showcase__group-title-text');
    groupTitleText.textContent = groupName ?? group.name;

    const readAll = document.createElement('span');
    readAll.classList.add('event-showcase__group-read-all');
    readAll.textContent = readAllLabel;

    const groupTitle = document.createElement('a');
    groupTitle.classList.add('event-showcase__group-title');
    groupTitle.href = group.url;
    groupTitle.appendChild(groupTitleText);
    groupTitle.appendChild(readAll);
    view.appendChild(groupTitle);

    const slideWrapper = document.createElement('div');
    slideWrapper.classList.add('event-showcase__group-event-wrapper', 'swiper-wrapper');

    group.events.forEach(event => {
      const slide = document.createElement('div')
      slide.classList.add('event-showcase__group-event-slide', 'swiper-slide');
      slide.append(this.buildEventView(event));
      slideWrapper.appendChild(slide);
    });

    const list = document.createElement('div');
    list.classList.add('event-showcase__group-event', 'swiper');
    list.appendChild(slideWrapper);
    view.appendChild(list);

    const prevButton = document.createElement('div');
    prevButton.classList.add('swiper-button-prev');
    list.appendChild(prevButton);

    const nextButton = document.createElement('div');
    nextButton.classList.add('swiper-button-next');
    list.appendChild(nextButton);

    const pagination = document.createElement('div');
    pagination.classList.add('swiper-pagination');
    list.appendChild(pagination);

    const scrollbar = document.createElement('div');
    scrollbar.classList.add('swiper-scrollbar');
    list.appendChild(scrollbar);

    return view;
  }

  /**
   * ショーケースの画面を構築します。
   */
  buildMainView(groups) {
    const view = document.createElement('div');
    view.classList.add('event-showcase');

    groups.forEach(group => {
      view.appendChild(this.buildGroupView(group, this.condition, this.groupName, this.readAllLabel));
    });

    return view;
  }

  /**
   * ショーケースを表示します。
   */
  async render() {
    // イベントを取得して、
    const events = await this.fetchEvents(this.condition);
    this.onEventLoaded(events);
    // イベントがない場合は表示しない
    if (events.length === 0) {
      return;
    }

    // グループ化して、
    const groups = this.sortGroup(this.groupEvent(events, this.grouping), this.grouping);

    // DOM を構築。
    this.container.appendChild(this.buildMainView(groups));

    // スライドショーを生成
    document.querySelectorAll('.swiper').forEach(function (element) {
      const nextButton = '.swiper-button-next';
      const prevButton = '.swiper-button-prev';
      const scrollbar = '.swiper-scrollbar';
      const slides = element.querySelectorAll('.swiper-slide');
      if (slides.length <= 1) {
        const next = element.querySelector(nextButton);
        const prev = element.querySelector(prevButton);
        if (next) next.style.display = 'none';
        if (prev) prev.style.display = 'none';
      }
      new Swiper(element, {
        modules: [Autoplay, Navigation, Pagination, Scrollbar],
        direction: 'horizontal',
        slidesPerView: 1,
        centeredSlides: 'false',
        loop: slides.length > 2,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        spaceBetween: 3,
        navigation: slides.length > 1 ? {
          nextEl: nextButton,
          prevEl: prevButton,
          clickable: true,
        } : false,
        scrollbar: {
          el: scrollbar,
          hide: true,
          draggable: true
        },
        breakpoints: {
          768: {
            slidesPerView: 'auto',
            centeredSlides: false,
            loop: false,
            autoplay: false,
            spaceBetween: 18,
            navigation: slides.length > 1 ? {
              nextEl: nextButton,
              prevEl: prevButton,
              enabled: true,
              clickable: true,
            } : false,
          },
        },
      });
    });
  }
  /**
   * イベント一覧データだけ返します
   */
  async getEventData() {
      // イベントを取得
      const events = await this.fetchEvents(this.condition);
      // イベントがない場合は表示しない
      if (events.length === 0) {
        return;
      }
      // イベントデータを返す
      this.onEventLoaded(events);
  }
}

window.EventShowcase = EventShowcase;

// Swiperのモジュールをページ上で呼び出させるため処理
window.Swiper = Swiper;
window.swiperModules = {
  A11y,
  Autoplay,
  Controller,
  EffectCoverflow,
  EffectCube,
  EffectFade,
  EffectFlip,
  EffectCreative,
  EffectCards,
  HashNavigation,
  History,
  Keyboard,
  Lazy,
  Mousewheel,
  Navigation,
  Pagination,
  Parallax,
  Scrollbar,
  Thumbs,
  Virtual,
  Zoom,
  FreeMode,
  Grid,
  Manipulation,
};

document.addEventListener('DOMContentLoaded', () => {
  AOS.init();
});
